/**
 * Surface-aware routing helpers (RSW-326 / subdomain routing ADR).
 */
import { getAppSurface } from './appSurface';

/** @typedef {'all' | 'portal' | 'admin' | 'jobseeker'} AppSurface */

const env = import.meta.env;

function normalizePublicUrl(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed.replace(/\/+$/, '') : '';
}

export function readRuntimePublicUrls() {
  if (typeof window === 'undefined') {
    return { portal: '', admin: '', jobseeker: '' };
  }
  const urls = window.__RSWORKS_RUNTIME_CONFIG__?.publicUrls;
  const fromRuntime = urls && typeof urls === 'object'
    ? {
      portal: normalizePublicUrl(urls.portal),
      admin: normalizePublicUrl(urls.admin),
      jobseeker: normalizePublicUrl(urls.jobseeker),
    }
    : { portal: '', admin: '', jobseeker: '' };

  return {
    portal: fromRuntime.portal || normalizePublicUrl(env.VITE_PUBLIC_PORTAL_URL),
    admin: fromRuntime.admin || normalizePublicUrl(env.VITE_PUBLIC_ADMIN_URL),
    jobseeker: fromRuntime.jobseeker || normalizePublicUrl(env.VITE_PUBLIC_JOBSEEKER_URL),
  };
}

/**
 * Infer portal host from admin hostname (e.g. admin.staging.rsworks.app → portal.staging…).
 * Used when runtime publicUrls are missing but DNS follows ADR conventions.
 */
export function inferPortalUrlFromHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:',
) {
  if (!hostname) return '';
  const scheme = protocol || 'https:';

  if (hostname.startsWith('admin.')) {
    return `${scheme}//${hostname.replace(/^admin\./, 'portal.')}`;
  }

  return '';
}

/**
 * Infer jobseeker host from portal/admin hostname (portal.* → my.* per ADR).
 */
export function inferJobseekerUrlFromHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:',
) {
  if (!hostname) return '';
  const scheme = protocol || 'https:';

  if (hostname.startsWith('portal.')) {
    return `${scheme}//${hostname.replace(/^portal\./, 'my.')}`;
  }

  if (hostname.startsWith('admin.')) {
    return `${scheme}//${hostname.replace(/^admin\./, 'my.')}`;
  }

  return '';
}

/** Portal URL for cross-surface redirects (runtime config → Vite → hostname inference). */
export function resolvePortalPublicUrl() {
  const configured = readRuntimePublicUrls().portal;
  if (configured) return configured;
  return inferPortalUrlFromHostname();
}

/**
 * Infer admin host from portal hostname (portal.staging.rsworks.app → admin.staging…).
 */
export function inferAdminUrlFromHostname(
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
  protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:',
) {
  if (!hostname) return '';
  const scheme = protocol || 'https:';

  if (hostname.startsWith('portal.')) {
    return `${scheme}//${hostname.replace(/^portal\./, 'admin.')}`;
  }

  return '';
}

/** Admin URL for cross-surface links (runtime config → Vite → hostname inference). */
export function resolveAdminPublicUrl() {
  const configured = readRuntimePublicUrls().admin;
  if (configured) return configured;
  return inferAdminUrlFromHostname();
}

/** Jobseeker URL for cross-surface redirects (runtime config → Vite → hostname inference). */
export function resolveJobseekerPublicUrl() {
  const configured = readRuntimePublicUrls().jobseeker;
  if (configured) return configured;
  return inferJobseekerUrlFromHostname();
}

/** Origin for counselor-generated /access/{guid} links (always the jobseeker host). */
export function resolveJobseekerAccessOrigin() {
  const configured = resolveJobseekerPublicUrl();
  if (configured) return configured;
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
}

export function normalizePathname(pathname = '') {
  if (!pathname || pathname === '/') return '/';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/';
}

/**
 * Whether a pathname is served on the given app surface.
 * @param {string} pathname
 * @param {AppSurface} [surface]
 */
export function isPathAllowedForSurface(pathname, surface = getAppSurface()) {
  if (surface === 'all') return true;

  const path = normalizePathname(pathname);

  if (surface === 'jobseeker') {
    if (path === '/') return true;
    if (path.startsWith('/access/')) return true;
    if (path === '/workspace' || path === '/tracker' || path === '/jobs' || path === '/profile'
      || path === '/settings' || path === '/cover' || path === '/interview'
      || path === '/assessments') return true;
    if (path === '/app' || path.startsWith('/app/')) return true;
    return false;
  }

  if (surface === 'portal') {
    if (path === '/') return true;
    if (path === '/caseload' || path === '/team' || path === '/job-board') return true;
    if (path.startsWith('/jobseekers/')) return true;
    if (path === '/dashboard' || path.startsWith('/dashboard/')) return true;
    if (path === '/counselor' || path.startsWith('/counselor/')) return true;
    return false;
  }

  if (surface === 'admin') {
    if (path === '/') return true;
    if (path === '/admin' || path.startsWith('/admin/')) return true;
    return false;
  }

  return true;
}

/**
 * Login shell mode for AppShell on `/`.
 * @param {AppSurface} [surface]
 */
export function loginModeForSurface(surface = getAppSurface()) {
  if (surface === 'jobseeker') return 'jobseeker';
  if (surface === 'portal' || surface === 'admin') return 'counselor';
  return 'all';
}

/** Auth0 SSO is only used on workforce surfaces. */
export function shouldMountAuth0(surface = getAppSurface()) {
  return surface === 'all' || surface === 'portal' || surface === 'admin';
}

/**
 * In-app or absolute redirect after authentication.
 * @param {{ userType: string, userRole?: string, surface?: AppSurface }} input
 * @returns {string}
 */
export function resolvePostLoginPath({
  userType,
  userRole,
  surface = getAppSurface(),
}) {
  if (userType === 'JOBSEEKER') {
    if (surface === 'all' || surface === 'jobseeker') {
      return '/workspace';
    }
    return resolveJobseekerPublicUrl() || '/';
  }

  if (surface === 'admin') {
    if (userRole === 'PLATFORM_ADMIN') return '/admin/partners';
    return resolvePortalPublicUrl() || '/';
  }

  return '/caseload';
}

/** Navigate to an absolute URL when set; otherwise use in-app fallback. */
export function assignLocationIfAbsolute(target, fallback = '/') {
  if (typeof target === 'string' && /^https?:\/\//i.test(target)) {
    window.location.assign(target);
    return true;
  }
  return false;
}
